'use client'

import * as React from 'react'

import { useMemo } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { shaderMaterial, useTrailTexture } from '@react-three/drei'
import * as THREE from 'three'
import { useMedia } from '~/hooks/use-media'

// Custom shader material
const DotMaterial = shaderMaterial(
  {
    time: 0,
    resolution: new THREE.Vector2(),
    dotColor: new THREE.Color('#787878'),
    bgColor: new THREE.Color('#121212'),
    mouseTrail: null,
    render: 0,
    rotation: 0,
    gridSize: 120,
  },
  /* glsl */ `
    void main() {
      gl_Position = vec4(position.xy, 0.0, 1.0);
    }
  `,
  /* glsl */ `
    uniform float time;
    uniform int render;
    uniform vec2 resolution;
    uniform vec3 dotColor;
    uniform vec3 bgColor;
    uniform sampler2D mouseTrail;
    uniform float rotation;
    uniform float gridSize;

    vec2 rotate(vec2 uv, float angle) {
        float s = sin(angle);
        float c = cos(angle);
        mat2 rotationMatrix = mat2(c, -s, s, c);
        return rotationMatrix * (uv - 0.5) + 0.5;
    }

    vec2 coverUv(vec2 uv) {
      vec2 s = resolution.xy / max(resolution.x, resolution.y);
      vec2 newUv = (uv - 0.5) * s + 0.5;
      return clamp(newUv, 0.0, 1.0);
    }

    // SDF for a rounded square
    float sdfRoundedSquare(vec2 p, float size, float radius) {
        vec2 d = abs(p - 0.5) - size + radius;
        return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0) - radius;
    }

    void main() {
      vec2 screenUv = gl_FragCoord.xy / resolution;
      vec2 uv = coverUv(screenUv);

      vec2 rotatedUv = rotate(uv, rotation);

      // Create a grid
      vec2 gridUv = fract(rotatedUv * gridSize);
      vec2 gridUvCenterInScreenCoords = rotate((floor(rotatedUv * gridSize) + 0.5) / gridSize, -rotation);

      // Mouse trail effect
      float mouseInfluence = texture2D(mouseTrail, gridUvCenterInScreenCoords).r;

      // Rounded square parameters
      float size = 0.25; // Base size of the square
      float radius = 0.05; // Corner radius of the square

      // Calculate the SDF for the rounded square
      float sdfSquare = sdfRoundedSquare(gridUv, size, radius);

      // Smooth edges and visibility based on mouse influence
      float smoothSquare = smoothstep(0.1, 0.0, sdfSquare);
      float visibility = mouseInfluence;

      // Adjust opacity
      float opacity = 0.4; // Reduced global opacity multiplier (e.g., 0.1 for subtle effect)

      // Mix background color with square color, applying reduced opacity
      vec3 composition = mix(bgColor, dotColor, smoothSquare * visibility * opacity);

      gl_FragColor = vec4(composition, 1.0); // Final color
    }
  `
)

function Scene() {
  const size = useThree((s) => s.size)
  const viewport = useThree((s) => s.viewport)

  const [trail, onMove] = useTrailTexture({
    size: 512,
    radius: 0.1,
    maxAge: 400,
    interpolate: 1,
  })

  const dotMaterial = useMemo(() => {
    return new DotMaterial()
  }, [])

  useFrame((state) => {
    if (!dotMaterial.uniforms.time) return
    dotMaterial.uniforms.time.value = state.clock.elapsedTime
  })

  const scale = Math.max(viewport.width, viewport.height) / 2

  return (
    <mesh scale={[scale, scale, 1]} onPointerMove={onMove}>
      <planeGeometry args={[2, 2]} />
      <primitive
        object={dotMaterial}
        resolution={[size.width * viewport.dpr, size.height * viewport.dpr]}
        rotation={0}
        gridSize={120}
        mouseTrail={trail}
        render={1}
      />
    </mesh>
  )
}

export const Events = () => {
  const set = useThree((s) => s.set)
  const get = useThree((s) => s.get)

  React.useEffect(() => {
    const { events } = get()
    if (!events) return
    const target = document.querySelector('body')

    if (!target) return

    let targetRect = target.getBoundingClientRect()

    const resizeObserver = new ResizeObserver(() => {
      targetRect = target.getBoundingClientRect()
    })

    resizeObserver.observe(target)

    const mainRect = targetRect

    set({
      events: {
        ...events,
        compute(event, state) {
          const x = event.clientX - mainRect.left
          const y = event.clientY - mainRect.top + window.scrollY
          state.pointer.set(
            (x / state.size.width) * 2 - 1,
            -(y / state.size.height) * 2 + 1
          )
          state.raycaster.setFromCamera(state.pointer, state.camera)
        },
      },
    })
    events.connect?.(target)

    return () => {
      resizeObserver.disconnect()
    }
  }, [get, set])

  return null
}

export const DotScreenShader = () => {
  const isDesktop = useMedia('(min-width: 1024px)')

  if (!isDesktop) return null

  return (
    <React.Suspense>
      <Canvas
        gl={{
          antialias: true,
          powerPreference: 'high-performance',
          outputColorSpace: THREE.SRGBColorSpace,
          toneMapping: THREE.NoToneMapping,
        }}
      >
        <Events />
        <Scene />
      </Canvas>
    </React.Suspense>
  )
}
